import { Container, Eyebrow, MButton, ArrowRight, MarketingPage, IMG } from "./ui";
import { SITE, faqJsonLd } from "./seo";

/* ─── Hero ────────────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="futuristic-grid relative overflow-hidden bg-warm-ink text-white">
      <div aria-hidden className="absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-warm-accent/20 blur-[120px]" />
      <div aria-hidden className="absolute right-[-10%] top-[-20%] h-[600px] w-[600px] rounded-full bg-cyan-300/10 blur-[140px]" />
      <Container className="relative grid min-h-[760px] items-center gap-12 py-16 lg:grid-cols-[.92fr_1.08fr] lg:py-20">
        <div className="relative z-10 animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[.06] px-3.5 py-1.5 text-xs font-medium uppercase tracking-[.16em] text-white/70 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-warm-glow shadow-[0_0_12px_#71E7DA]" />
            Human-led clinical intelligence
          </span>
          <h1 className="mt-7 max-w-3xl text-balance text-5xl font-semibold leading-[.98] tracking-[-.045em] sm:text-6xl md:text-7xl">
            The doctor who arrives{" "}<span className="font-display italic font-normal text-warm-glow">already informed.</span>
          </h1>
          <p className="mt-7 max-w-xl text-lg leading-relaxed text-white/65">
          WonderfulDoctor reads a patient's labs, imaging and history before the call, runs a natural AI video
          consultation, streams live vitals into the room, and routes every visit to a licensed clinician for
          sign-off.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
          <MButton to="/signup" className="px-6 py-3 text-base">
            Start free <ArrowRight />
          </MButton>
          <MButton to="/platform" variant="outline" className="border-white/20 bg-white/[.06] px-6 py-3 text-base text-white hover:border-white/50">
            See how it works
          </MButton>
          </div>
          <p className="mt-4 text-sm text-white/40">Pay only for completed consultations · No seats · No subscription</p>
          <div className="mt-10 flex items-center gap-5 border-t border-white/10 pt-6 text-xs uppercase tracking-[.14em] text-white/40">
            <span>HIPAA-grade</span><span className="h-1 w-1 rounded-full bg-warm-glow" /><span>Clinician reviewed</span><span className="h-1 w-1 rounded-full bg-warm-glow" /><span>24/7</span>
          </div>
        </div>
        <div className="relative min-h-[500px] lg:min-h-[630px]">
          <div className="absolute inset-0 overflow-hidden rounded-[2.25rem] border border-white/15 bg-white/5 shadow-[0_40px_100px_rgba(0,0,0,.4)]">
          <img
            src={IMG.hero}
            alt="Physician speaking attentively with a patient in a modern clinic"
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-warm-ink/45 via-transparent to-transparent" />

          {/* floating visit card */}
          <div className="glass-panel absolute bottom-5 left-5 w-[285px] rounded-2xl border border-white/60 p-4 text-warm-ink sm:bottom-7 sm:left-7">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-warm-muted">Visit summary</span>
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                Clinician signed
              </span>
            </div>
            <div className="mt-3 flex items-end gap-2">
              <span className="text-lg font-bold tracking-tight text-warm-ink">Routine</span>
              <span className="pb-0.5 text-xs text-warm-muted">triage · follow-up in 2 wks</span>
            </div>
            <div className="mt-3 space-y-1.5">
              {[
                ["Reports read", "3 of 3"],
                ["Live vitals", "HR 72 · SpO₂ 98%"],
                ["Pre-visit brief", "Ready"],
              ].map(([label, v]) => (
                <div key={label} className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-warm-muted">{label}</span>
                  <span className="text-[11px] font-medium text-warm-ink">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* floating live badge */}
          <div className="absolute right-5 top-5 flex items-center gap-2 rounded-full border border-white/35 bg-warm-ink/70 px-3 py-1.5 text-xs font-medium text-white shadow-lg backdrop-blur-xl sm:right-7 sm:top-7">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warm-accent/60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-warm-accent" />
            </span>
            Consultation in progress
          </div>
          </div>
          <div className="absolute -right-5 bottom-24 hidden rounded-2xl border border-white/15 bg-warm-ink/80 p-4 text-white shadow-2xl backdrop-blur-xl sm:block">
            <div className="text-[10px] uppercase tracking-[.16em] text-white/45">Live signal</div>
            <div className="mt-2 flex items-center gap-3"><span className="text-2xl font-semibold">98%</span><span className="text-xs text-white/50">SpO₂<br/>stable</span></div>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ─── Logo / trust strip ──────────────────────────────────────────────────── */
function TrustStrip() {
  const stats: [string, string][] = [
    ["3 min", "to configure an AI doctor"],
    ["24/7", "patient self-scheduling"],
    ["< 2 min", "to a signed visit summary"],
    ["HIPAA", "grade safeguards"],
  ];
  return (
    <section className="border-b border-warm-border bg-white">
      <Container className="grid grid-cols-2 gap-y-8 py-10 md:grid-cols-4">
        {stats.map(([big, small]) => (
          <div key={small} className="text-center">
            <div className="text-3xl font-semibold tracking-[-.04em] text-warm-ink">{big}</div>
            <div className="mt-1 text-sm text-warm-muted">{small}</div>
          </div>
        ))}
      </Container>
    </section>
  );
}

/* ─── How it works ────────────────────────────────────────────────────────── */
function HowItWorks() {
  const steps: { n: string; title: string; body: string }[] = [
    {
      n: "01",
      title: "Configure your AI doctor",
      body: "Choose a specialty, set the clinical model — AI-only or AI with clinician sign-off — pick your region and consult length. We bind it to a medical persona and a lifelike video avatar.",
    },
    {
      n: "02",
      title: "Patients book & upload reports",
      body: "Patients self-schedule by email, then upload labs, imaging and prior notes. Our ingestion pipeline extracts the findings and writes a structured pre-visit brief before the call.",
    },
    {
      n: "03",
      title: "Consult, then sign off",
      body: "The AI doctor joins the video call already briefed, reasons over the patient's history, and reacts to live vitals. Out comes a SOAP visit summary, triage and next steps — routed for clinician review.",
    },
  ];
  return (
    <section className="py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            From booking to a signed visit summary — on autopilot.
          </h2>
          <p className="mt-4 text-lg text-warm-muted">
            Intake, report ingestion, the video consult and the write-up are all automated. Your clinicians spend
            their judgment where it matters: reviewing and signing.
          </p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {steps.map((s) => (
            <div
              key={s.n}
              className="group rounded-3xl border border-warm-border bg-warm-card p-7 transition-shadow hover:shadow-[0_20px_50px_-25px_rgba(36,31,25,0.3)]"
            >
              <div className="font-display text-5xl italic text-warm-glow transition-colors group-hover:text-warm-accent">
                {s.n}
              </div>
              <h3 className="mt-5 text-xl font-semibold text-warm-ink">{s.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-warm-muted">{s.body}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ─── Feature grid ────────────────────────────────────────────────────────── */
function Icon({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-6 w-6">
      <path d={path} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Features() {
  const features: { title: string; body: string; icon: string }[] = [
    {
      title: "AI video doctor",
      body: "A lifelike medical avatar holds a natural, low-latency conversation, asks adaptive follow-ups, and runs entirely in the browser — no install for patients.",
      icon: "M15 10l4.5-2.5v9L15 14M4 7h11v10H4z",
    },
    {
      title: "Reports become context",
      body: "Labs, imaging, DICOM and prior notes are extracted and summarized into a pre-visit brief. Imaging yields observations for clinician review — never an autonomous diagnosis.",
      icon: "M7 3h7l5 5v13H7zM14 3v5h5M9 13h6M9 17h6",
    },
    {
      title: "Longitudinal health journal",
      body: "An append-only record of every visit, report, medication and vital. The AI doctor reasons over it with retrieval and memory, so it remembers prior visits.",
      icon: "M6 4h11l3 3v13H6zM9 9h7M9 13h7M9 17h4",
    },
    {
      title: "Real-time vitals",
      body: "Heart rate, SpO₂, blood pressure and temperature stream in from BLE devices, HealthKit or manual entry and update the doctor live — dangerous readings escalate at once.",
      icon: "M3 12h4l2 5 4-12 2 7h6",
    },
    {
      title: "Clinician sign-off & triage",
      body: "Red-flag detection and emergency escalation are built in. Every consult can route to a human review-and-sign queue before the summary reaches the patient.",
      icon: "M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z M9 12l2 2 4-4",
    },
    {
      title: "Pay-as-you-go billing",
      body: "Add a card once. You're billed per completed consultation at a transparent, duration-based price. No seats, no subscription, no waste.",
      icon: "M3 10h18M3 6h18v12H3zM7 15h4",
    },
  ];
  return (
    <section className="bg-warm-sand/40 py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>The platform</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Everything a virtual visit needs — and nothing it shouldn't do alone.
          </h2>
        </div>
        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-warm-border bg-warm-border sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="bg-warm-card p-7 transition-colors hover:bg-warm-glow/10">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-warm-accent/10 text-warm-accent">
                <Icon path={f.icon} />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-warm-ink">{f.title}</h3>
              <p className="mt-2.5 text-[15px] leading-relaxed text-warm-muted">{f.body}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ─── Safety split section ────────────────────────────────────────────────── */
function Safety() {
  const signals: [string, string][] = [
    ["Emergency detection", "Red-flag symptoms and vitals — chest pain, SpO₂ below 90, suicidal ideation — trigger an urgent banner and escalate to a human immediately."],
    ["Source-grounded answers", "Every statement cites the report or finding it's based on. The doctor never invents values or asserts a read it can't support."],
    ["Clinician in the loop", "Where your model requires it, a licensed clinician reviews and signs the visit note before it's final."],
    ["Full disclosure & consent", "Patients always know they're speaking with an AI, and consent for telehealth and recording is captured up front."],
  ];
  return (
    <section className="py-24">
      <Container className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <Eyebrow>Safety, by design</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Information and triage — not a diagnosis the machine makes alone.
          </h2>
          <p className="mt-4 text-lg text-warm-muted">
            The AI doctor is bounded by clinical-safety guardrails and, where required, human clinician oversight.
            It informs and triages; it never delays care or replaces a licensed clinician's judgment.
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {signals.map(([t, d]) => (
              <div key={t}>
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-warm-accent/15 text-warm-accent">
                    <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <h3 className="font-semibold text-warm-ink">{t}</h3>
                </div>
                <p className="mt-1.5 pl-7 text-sm leading-relaxed text-warm-muted">{d}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="overflow-hidden rounded-4xl border border-warm-border shadow-[0_30px_80px_-30px_rgba(36,31,25,0.35)]">
            <img
              src={IMG.mist}
              alt="Two clinicians reviewing a patient summary together"
              className="h-[460px] w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 hidden w-56 rounded-2xl border border-warm-border bg-warm-card p-4 shadow-xl sm:block">
            <div className="text-xs font-medium uppercase tracking-wide text-warm-muted">Triage</div>
            <div className="mt-2 flex items-end gap-2">
              <span className="text-3xl font-bold text-emerald-600">Routine</span>
            </div>
            <p className="mt-1 text-xs text-warm-muted">no red flags · clinician sign-off complete</p>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ─── Specialties ─────────────────────────────────────────────────────────── */
function Specialties() {
  const types: string[] = [
    "General medicine",
    "Urgent care",
    "Mental health",
    "Dermatology",
    "Pediatrics",
    "Cardiology",
    "Endocrinology",
    "Women's health",
  ];
  return (
    <section className="py-24">
      <Container>
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <Eyebrow>One platform, every specialty</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              Built for <span className="font-display italic text-warm-accent">every kind</span> of visit.
            </h2>
            <p className="mt-4 text-lg text-warm-muted">
              Pick a specialty, set the persona, region and consult length. We assemble the clinical context and
              safety guardrails — then the AI doctor adapts the conversation to each patient.
            </p>
            <div className="mt-7">
              <MButton to="/platform" variant="outline">
                Explore the platform <ArrowRight />
              </MButton>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {types.map((t) => (
              <span
                key={t}
                className="rounded-full border border-warm-border bg-warm-card px-4 py-2.5 text-sm font-medium text-warm-ink"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ─── Pricing ─────────────────────────────────────────────────────────────── */
function Pricing() {
  const tiers: { mins: string; price: string; blurb: string; featured?: boolean }[] = [
    { mins: "15 min", price: "$17", blurb: "Quick check-ins & follow-ups" },
    { mins: "30 min", price: "$34", blurb: "Standard consultations", featured: true },
    { mins: "45 min", price: "$50.50", blurb: "Complex, multi-issue visits" },
    { mins: "60 min", price: "$67.50", blurb: "Thorough, in-depth consultations" },
  ];
  return (
    <section id="pricing" className="bg-warm-ink py-24 text-warm-bg">
      <Container>
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-warm-bg/15 px-3 py-1 text-xs font-medium uppercase tracking-[0.14em] text-warm-bg/70">
            <span className="h-1.5 w-1.5 rounded-full bg-warm-glow" />
            Pay-as-you-go
          </span>
          <h2 className="mt-5 text-4xl font-bold tracking-tight sm:text-5xl">
            Priced by the minute. Billed only when complete.
          </h2>
          <p className="mt-4 text-lg text-warm-bg/60">
            One transparent price per consultation, set by its length and locked when you book. No seats, no
            subscription — you pay when a visit finishes.
          </p>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {tiers.map((t) => (
            <div
              key={t.mins}
              className={`rounded-3xl border p-6 ${
                t.featured
                  ? "border-warm-accent bg-warm-accent/10 ring-1 ring-warm-accent"
                  : "border-warm-bg/12 bg-warm-bg/[0.04]"
              }`}
            >
              {t.featured && (
                <span className="mb-3 inline-block rounded-full bg-warm-accent px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                  Most popular
                </span>
              )}
              <div className="text-sm font-medium text-warm-bg/60">{t.mins} consult</div>
              <div className="mt-2 flex items-end gap-1">
                <span className="text-4xl font-bold tracking-tight">{t.price}</span>
                <span className="pb-1 text-sm text-warm-bg/50">/ consult</span>
              </div>
              <p className="mt-3 text-sm text-warm-bg/60">{t.blurb}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-col items-center gap-3">
          <div className="flex flex-col gap-3 sm:flex-row">
            <MButton to="/signup" className="px-6 py-3 text-base">
              Launch your first AI doctor <ArrowRight />
            </MButton>
            <MButton
              to="/pricing"
              variant="outline"
              className="border-warm-bg/30 bg-transparent px-6 py-3 text-base text-warm-bg hover:border-warm-bg"
            >
              See full pricing
            </MButton>
          </div>
          <p className="text-sm text-warm-bg/45">
            Prices include the AI doctor, report ingestion, the visit summary, scheduling and emails. Volume rates available.
          </p>
        </div>
      </Container>
    </section>
  );
}

/* ─── Closing CTA ─────────────────────────────────────────────────────────── */
function ClosingCTA() {
  return (
    <section className="py-24">
      <Container>
        <div className="relative overflow-hidden rounded-4xl border border-warm-border">
          <img src={IMG.lake} alt="Doctor in conversation with a patient" className="absolute inset-0 h-full w-full object-cover object-right" />
          <div className="absolute inset-0 bg-gradient-to-r from-warm-ink via-warm-ink/90 to-warm-ink/15" />
          <div className="relative px-8 py-16 sm:px-16 sm:py-24">
            <h2 className="max-w-2xl text-4xl font-bold leading-tight tracking-tight text-warm-bg sm:text-5xl">
              Give every patient a doctor who's already read their chart.
            </h2>
            <p className="mt-4 max-w-xl text-lg text-warm-bg/75">
              Launch your first AI doctor in minutes. No waiting rooms, no missed history — just an informed,
              clinician-backed consultation for everyone you see.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <MButton to="/signup" className="px-6 py-3 text-base">
                Get started free <ArrowRight />
              </MButton>
              <MButton href="mailto:hello@wonderfulagent.ai" variant="outline" className="border-warm-bg/30 bg-transparent px-6 py-3 text-base text-warm-bg hover:border-warm-bg">
                Talk to us
              </MButton>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

const HOME_FAQS: { q: string; a: string }[] = [
  {
    q: "What is WonderfulDoctor?",
    a: "A pay-as-you-go AI telehealth platform. You configure an AI video doctor, patients self-schedule by email and upload their reports, the AI doctor runs a recorded video consultation with the patient's full picture — reports, history and live vitals — and a licensed clinician reviews and signs off where required.",
  },
  {
    q: "How much does it cost?",
    a: "You pay per completed consultation, priced by duration: $17 for 15 minutes, $34 for 30, $50.50 for 45, and $67.50 for 60. No seats and no subscription — you're billed only when a visit completes.",
  },
  {
    q: "Does the AI make a diagnosis?",
    a: "No. The AI doctor provides information and triage, not a definitive diagnosis or prescription. Where your clinical model requires it, a licensed clinician reviews and signs every visit, and emergencies are escalated to a human immediately.",
  },
  {
    q: "Is my patients' health data safe?",
    a: "WonderfulDoctor is built with HIPAA-grade safeguards: encryption at rest and in transit, strict per-patient access scoping, consent capture, an immutable audit log, and data export or deletion on request.",
  },
];

const HOME_JSONLD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: SITE.name,
  applicationCategory: "HealthApplication",
  operatingSystem: "Web",
  url: SITE.url,
  description:
    "Pay-as-you-go AI telehealth platform: AI video consultations, clinical report ingestion, a longitudinal health journal, real-time vitals and licensed-clinician sign-off.",
  offers: {
    "@type": "Offer",
    price: "17.00",
    priceCurrency: "USD",
    description: "Per completed consultation, from $17 (15 min). No seats, no subscription.",
  },
};

export function Home() {
  return (
    <MarketingPage
      seo={{
        title: "WonderfulDoctor — AI video consultations, clinician-reviewed",
        description:
          "Pay-as-you-go AI telehealth platform. Configure an AI video doctor that reads patient reports, reacts to live vitals, and routes every visit to a licensed clinician for sign-off.",
        path: "/",
        jsonLd: [HOME_JSONLD, faqJsonLd(HOME_FAQS)],
      }}
    >
      <Hero />
      <TrustStrip />
      <HowItWorks />
      <Features />
      <Safety />
      <Specialties />
      <Pricing />
      <ClosingCTA />
    </MarketingPage>
  );
}
